import { create } from 'zustand'
import { authApi } from '@/api/endpoints'
import { tokenStore } from '@/api/client'

export const useAuth = create((set, get) => ({
  user: null,
  loading: true,

  /** Restore a session on page load from the stored token. */
  init: async () => {
    if (!tokenStore.get()) {
      set({ user: null, loading: false })
      return
    }
    try {
      set({ user: await authApi.me(), loading: false })
    } catch {
      tokenStore.clear()
      set({ user: null, loading: false })
    }
  },

  login: async (email, password, recaptchaToken) => {
    const data = await authApi.login(email, password, recaptchaToken)
    tokenStore.set(data.access_token)
    set({ user: data.user, loading: false })
    return data.user
  },

  logout: () => {
    tokenStore.clear()
    set({ user: null })
  },

  /**
   * Re-read the session. Call it after an action that can change what the
   * signed-in user may do — handing the admin role to someone else, say —
   * so the sidebar stops offering screens the API now refuses.
   */
  refresh: async () => {
    if (!tokenStore.get()) return null
    const user = await authApi.me()
    set({ user })
    return user
  },
}))

/**
 * The permission names the API gates on, mirrored here only so the UI has
 * something to refer to. Which of them a user holds arrives on the user,
 * resolved from the database — never derive it from the role name here, or
 * a grant changed in the portal's own role screen would not be respected.
 */
export const PERMS = {
  dashboardRead: 'dashboard:read',
  customersRead: 'customers:read',
  productsRead: 'products:read',
  ordersRead: 'orders:read',
  messagesRead: 'messages:read',
  reportsRead: 'reports:read',
  aiRead: 'ai:read',
  customersWrite: 'customers:write',
  customersDelete: 'customers:delete',
  productsWrite: 'products:write',
  ordersWrite: 'orders:write',
  ordersStatus: 'orders:status',
  deliveriesRead: 'deliveries:read',
  deliveriesWrite: 'deliveries:write',
  transportLoadsRead: 'transport_loads:read',
  invoicesRead: 'invoices:read',
  invoicesCorrect: 'invoices:correct',
  messagesReprocess: 'messages:reprocess',
  playgroundRun: 'playground:run',
  usersRead: 'users:read',
  usersManage: 'users:manage',
  rolesRead: 'roles:read',
  settingsWrite: 'settings:write',
}

/** Does this user hold the permission? */
export const can = (user, permission) => Boolean(user?.permissions?.includes(permission))

/**
 * Changing what a role may do is not a permission — the API reserves it for
 * super admins, so that it cannot be granted to a role by mistake.
 */
export const isSuperAdmin = (user) => Boolean(user?.is_superuser)

/** Fallback only: the real label comes from the roles table. */
export const roleLabel = (user) =>
  user?.role_label ?? (user?.role ? user.role.replace(/_/g, ' ') : '')
